import React, { useState, useEffect } from 'react';
import { Send, MessageCircle, CheckCircle2, AlertCircle, Sparkles, RefreshCw, ExternalLink, Printer, Building2, Clock, ShieldCheck } from 'lucide-react';

export type OABNotifyStage = 'EMITTED' | 'QUOTED' | 'APPROVED' | 'RAMP_RECEPTION';

export interface OABStageNotifyPanelProps {
  stage: OABNotifyStage;
  folio: string;
  oabId?: string;
  urgencySummary?: string;
  storesSummary?: string;
  linesCount: number;
  approvedLinesCount?: number;
  rejectedLinesCount?: number;
  routeSheetPrinted?: boolean;
  operatorName?: string;
  operatorRole?: string;
  vendorName?: string;
  quoteNumber?: string;
  estimatedDeliveryDate?: string;
  notes?: string;
  subOrders?: Array<{ folio: string; proveedor: string; lineCount: number }>;
  delegationReason?: string;
  rampSummary?: {
    nriFolio: string;
    actaRechazoFolio?: string | null;
    proveedor?: string;
    numeroNotaEntrega?: string;
    arrivedItems: Array<{ nombre: string; codigo?: string; cantidad: number; unidad?: string; ubicacion?: string }>;
    missingItems: Array<{ nombre: string; codigo?: string; cantidadFaltante: number; unidad?: string }>;
    rejectedItems: Array<{ nombre: string; codigo?: string; cantidadRechazada: number; motivo?: string; unidad?: string }>;
    transportista?: string;
    choferNombre?: string;
    placaVehiculo?: string;
    guiaRemision?: string;
    nriPrinted?: boolean;
    rejectionPrinted?: boolean;
  };
  compact?: boolean;
}

function getAuthHeaders(): Record<string, string> {
  if (typeof window === 'undefined') return { 'Content-Type': 'application/json' };
  const token = sessionStorage.getItem('sanesca_auth_jwt') || localStorage.getItem('sanesca_auth_jwt') || '';
  return token
    ? { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` }
    : { 'Content-Type': 'application/json' };
}

function formatShortTime(isoStr?: string): string {
  if (!isoStr) return '';
  try {
    const d = new Date(isoStr);
    return d.toLocaleTimeString('es-VE', { hour: '2-digit', minute: '2-digit' });
  } catch {
    return '';
  }
}

const STAGE_META: Record<OABNotifyStage, {
  badge: string;
  title: string;
  subtitle: string;
  telegramTopicLabel: string;
  targetQuery: string;
}> = {
  EMITTED: {
    badge: 'Etapa 1 • Solicitado',
    title: 'Notificación de Emisión Formal OAB y Hoja de Ruta',
    subtitle: 'Deja constancia de la solicitud de compras, estado de la Hoja Viajera y enlace directo para que Compras cotice.',
    telegramTopicLabel: 'Tópico 145 (Compras)',
    targetQuery: 'target=review'
  },
  QUOTED: {
    badge: 'Etapa 2 • Cotizada',
    title: 'Notificación de Cotización Lista para Aprobación Gerencial',
    subtitle: 'Notifica a Gerencia que Compras completó la cotización (sin exponer costos en el chat) para que autorice la compra.',
    telegramTopicLabel: 'Tópico 145 (Compras)',
    targetQuery: 'target=review'
  },
  APPROVED: {
    badge: 'Etapa 3 • En Compra',
    title: 'Notificación de Aprobación Gerencial para Ejecución de Compra',
    subtitle: 'Notifica a Compras y Almacén que la orden fue aprobada con enlaces duales para Compra y Recepción en Rampa.',
    telegramTopicLabel: 'Tópico 145 (Compras)',
    targetQuery: 'target=review'
  },
  RAMP_RECEPTION: {
    badge: 'Etapa 4 • Recepción en Rampa',
    title: 'Notificación Tripartita de Recepción en Muelle (NRI)',
    subtitle: 'Informa qué llegó hoy, qué falta por llegar (Backorder) y qué se devolvió al transportista con enlace al Historial.',
    telegramTopicLabel: 'Tópico 146 (Inventario)',
    targetQuery: 'target=ramp'
  }
};

export const OABStageNotifyPanel: React.FC<OABStageNotifyPanelProps> = ({
  stage,
  folio,
  oabId,
  urgencySummary = '🟢 Normal',
  storesSummary = '📦 Stock Común de Fábrica',
  linesCount,
  approvedLinesCount,
  rejectedLinesCount,
  routeSheetPrinted = false,
  operatorName = 'Operador de Planta',
  operatorRole = 'Operaciones',
  vendorName,
  quoteNumber,
  estimatedDeliveryDate,
  notes,
  subOrders,
  delegationReason,
  rampSummary,
  compact = false
}) => {
  const [sendingTelegram, setSendingTelegram] = useState(false);
  const [sendingWhatsapp, setSendingWhatsapp] = useState(false);
  const [sentTelegram, setSentTelegram] = useState<string | null>(null);
  const [sentWhatsapp, setSentWhatsapp] = useState<string | null>(null);
  const [persistedRouteSheetPrinted, setPersistedRouteSheetPrinted] = useState<boolean>(routeSheetPrinted);
  const [persistedNriPrinted, setPersistedNriPrinted] = useState<boolean>(Boolean(rampSummary?.nriPrinted));
  const [persistedRejectionPrinted, setPersistedRejectionPrinted] = useState<boolean>(Boolean(rampSummary?.rejectionPrinted));
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const meta = STAGE_META[stage];
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://sanesca-inventario.pages.dev';
  const directUrl = stage === 'RAMP_RECEPTION' && rampSummary?.nriFolio
    ? `${baseUrl}/?rampReceipt=${encodeURIComponent(rampSummary.nriFolio)}&folio=${encodeURIComponent(folio)}`
    : `${baseUrl}/?folio=${encodeURIComponent(folio)}&${meta.targetQuery}`;
  const rampDirectUrl = `${baseUrl}/?folio=${encodeURIComponent(folio)}&target=ramp`;

  const effectivePrinted = Boolean(routeSheetPrinted || persistedRouteSheetPrinted);
  const effectiveNriPrinted = Boolean(rampSummary?.nriPrinted || persistedNriPrinted);
  const effectiveRejectionPrinted = Boolean(rampSummary?.rejectionPrinted || persistedRejectionPrinted);
  const effectiveStageKey = stage === 'RAMP_RECEPTION' && rampSummary?.nriFolio
    ? `RAMP_RECEPTION:${rampSummary.nriFolio}`
    : stage;

  // Hidratar estado persistido de notificación e impresión desde Cloudflare KV (Aislado por NRI en Etapa 4 - PLAN-048)
  useEffect(() => {
    if (!folio) return;
    let cancelled = false;
    fetch(`/api/oab/notify-stage?action=status&folio=${encodeURIComponent(folio)}`)
      .then(res => res.ok ? res.json() : null)
      .then(data => {
        if (cancelled || !data?.log) return;
        if (data.log.routeSheetPrinted) {
          setPersistedRouteSheetPrinted(true);
        }
        const stageLog = stage === 'RAMP_RECEPTION' && rampSummary?.nriFolio
          ? data.log.stages?.[effectiveStageKey]
          : data.log.stages?.[stage];

        if (stageLog?.nriPrinted) setPersistedNriPrinted(true);
        if (stageLog?.rejectionPrinted) setPersistedRejectionPrinted(true);

        if (stageLog?.telegram) {
          const t = formatShortTime(stageLog.telegram.sentAt);
          setSentTelegram(`Enviado ${t ? `a las ${t} ` : ''}por ${stageLog.telegram.operatorName || 'Operador'}`);
        } else {
          setSentTelegram(null);
        }
        if (stageLog?.whatsapp) {
          const t = formatShortTime(stageLog.whatsapp.sentAt);
          setSentWhatsapp(`Enviado ${t ? `a las ${t} ` : ''}por ${stageLog.whatsapp.operatorName || 'Operador'}`);
        } else {
          setSentWhatsapp(null);
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [folio, stage, effectiveStageKey, rampSummary?.nriFolio]);

  // Persistir en KV cuando el operador imprime la Hoja de Ruta en Etapa 1
  useEffect(() => {
    if (routeSheetPrinted && folio && !persistedRouteSheetPrinted) {
      setPersistedRouteSheetPrinted(true);
      fetch('/api/oab/notify-stage', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          action: 'mark_printed',
          docType: 'ROUTE_SHEET',
          folio,
          operatorName
        })
      }).catch(() => {});
    }
  }, [routeSheetPrinted, folio, persistedRouteSheetPrinted, operatorName]);

  // Persistir en KV cuando el operador imprime la NRI Carta o el Acta de Rechazo en Etapa 4 (PLAN-048)
  useEffect(() => {
    if (stage === 'RAMP_RECEPTION' && rampSummary?.nriPrinted && folio && !persistedNriPrinted) {
      setPersistedNriPrinted(true);
      fetch('/api/oab/notify-stage', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          action: 'mark_printed',
          docType: 'NRI',
          folio,
          nriFolio: rampSummary.nriFolio,
          operatorName
        })
      }).catch(() => {});
    }
  }, [stage, rampSummary?.nriPrinted, rampSummary?.nriFolio, folio, persistedNriPrinted, operatorName]);

  useEffect(() => {
    if (stage === 'RAMP_RECEPTION' && rampSummary?.rejectionPrinted && folio && !persistedRejectionPrinted) {
      setPersistedRejectionPrinted(true);
      fetch('/api/oab/notify-stage', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          action: 'mark_printed',
          docType: 'REJECTION',
          folio,
          nriFolio: rampSummary.nriFolio,
          operatorName
        })
      }).catch(() => {});
    }
  }, [stage, rampSummary?.rejectionPrinted, rampSummary?.nriFolio, folio, persistedRejectionPrinted, operatorName]);

  const handleNotify = async (channel: 'telegram' | 'whatsapp') => {
    setErrorMsg(null);
    if (channel === 'telegram') setSendingTelegram(true);
    else setSendingWhatsapp(true);

    try {
      const response = await fetch('/api/oab/notify-stage', {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          channel,
          stage,
          folio,
          oabId,
          urgencySummary,
          storesSummary,
          linesCount,
          approvedLinesCount,
          rejectedLinesCount,
          routeSheetPrinted: effectivePrinted,
          operatorName,
          operatorRole,
          vendorName,
          quoteNumber,
          estimatedDeliveryDate,
          notes,
          subOrders,
          delegationReason,
          rampSummary: rampSummary
            ? {
                ...rampSummary,
                nriPrinted: effectiveNriPrinted,
                rejectionPrinted: effectiveRejectionPrinted
              }
            : undefined,
          directUrl
        })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok || data.error) {
        throw new Error(data.error || `Error HTTP ${response.status} al despachar notificación`);
      }

      const shortTime = formatShortTime(data.sentAt || new Date().toISOString());
      const resolvedOp = data.operatorName || operatorName;

      if (channel === 'telegram') {
        setSentTelegram(`Enviado ${shortTime ? `a las ${shortTime} ` : ''}por ${resolvedOp}`);
      } else {
        setSentWhatsapp(`Enviado ${shortTime ? `a las ${shortTime} ` : ''}por ${resolvedOp}`);
      }
    } catch (err: any) {
      setErrorMsg(err?.message || 'No se pudo completar el envío de la notificación.');
    } finally {
      if (channel === 'telegram') setSendingTelegram(false);
      else setSendingWhatsapp(false);
    }
  };

  return (
    <div className={`rounded-xl border border-brand-500/30 bg-surfaceHigh/90 ${compact ? 'p-3.5' : 'p-4'} space-y-3 shadow-lg`}>
      {/* Cabecera del Panel */}
      <div className="flex flex-wrap items-start justify-between gap-2">
        <div className="space-y-0.5">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider bg-brand-500/20 text-brand-300 border border-brand-500/40">
              <Sparkles className="w-3 h-3" />
              {meta.badge}
            </span>
            <span className="text-[10px] font-mono text-slate-400">
              Control Semi-Automático n8n
            </span>
          </div>
          <h4 className="text-xs sm:text-sm font-bold text-white pt-0.5">
            {meta.title}
          </h4>
          {!compact && (
            <p className="text-[11px] text-slate-300 leading-snug">
              {meta.subtitle}
            </p>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-1.5 shrink-0">
          <a
            href={directUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-surface text-[11px] font-mono text-sky-400 hover:text-sky-300 border border-borderSubtle hover:border-sky-500/40 transition"
            title="Enlace directo a Revisión OAB (Compras / Gerencia)"
          >
            <span>🔗 {stage === 'APPROVED' ? 'Compras' : folio}</span>
            <ExternalLink className="w-3 h-3" />
          </a>
          {stage === 'APPROVED' && (
            <a
              href={rampDirectUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg bg-surface text-[11px] font-mono text-emerald-400 hover:text-emerald-300 border border-borderSubtle hover:border-emerald-500/40 transition"
              title="Enlace directo a Terminal de Rampa (Almacén)"
            >
              <span>🚚 Rampa</span>
              <ExternalLink className="w-3 h-3" />
            </a>
          )}
        </div>
      </div>

      {/* Ficha de Variables que se incluirán en el Mensaje (Conteo Numérico Limpio y Sin Costos Expuestos) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 p-2.5 rounded-lg bg-page/80 border border-borderSubtle text-[11px]">
        <div className="flex items-start gap-2">
          <Clock className="w-3.5 h-3.5 text-amber-400 shrink-0 mt-0.5" />
          <div>
            <span className="text-slate-400">Urgencia y Conteo: </span>
            <span className="font-semibold text-slate-100">{urgencySummary}</span>
            <span className="text-slate-400 ml-1">({linesCount} ítem{linesCount === 1 ? '' : 's'})</span>
          </div>
        </div>

        <div className="flex items-start gap-2">
          <Building2 className="w-3.5 h-3.5 text-sky-400 shrink-0 mt-0.5" />
          <div className="truncate" title={storesSummary}>
            <span className="text-slate-400">Destino / Pedido: </span>
            <span className="font-semibold text-slate-100">{storesSummary}</span>
          </div>
        </div>

        {stage === 'EMITTED' && (
          <div className="flex items-start gap-2 sm:col-span-2">
            <Printer className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${effectivePrinted ? 'text-emerald-400' : 'text-amber-400'}`} />
            <div>
              <span className="text-slate-400">Constancia Hoja de Ruta: </span>
              <span className={`font-semibold ${effectivePrinted ? 'text-emerald-300' : 'text-amber-300'}`}>
                {effectivePrinted
                  ? '🖨️ Impresa y en ruta física'
                  : '⏳ Pendiente de impresión física (Notificación Digital)'}
              </span>
              <span className="text-slate-400 ml-2">• Emisor: <strong className="text-slate-200">{operatorName}</strong></span>
            </div>
          </div>
        )}

        {stage === 'RAMP_RECEPTION' && rampSummary && (
          <div className="flex items-start gap-2 sm:col-span-2">
            <Printer className={`w-3.5 h-3.5 shrink-0 mt-0.5 ${effectiveNriPrinted ? 'text-emerald-400' : 'text-amber-400'}`} />
            <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1">
              <span>
                <span className="text-slate-400">Folio NRI: </span>
                <strong className="font-mono text-emerald-300">{rampSummary.nriFolio}</strong>
              </span>
              <span>
                <span className="text-slate-400">NRI Carta: </span>
                <strong className={effectiveNriPrinted ? 'text-emerald-300' : 'text-amber-300'}>
                  {effectiveNriPrinted ? '✅ Impresa' : '⏳ Sin imprimir'}
                </strong>
              </span>
              {rampSummary.actaRechazoFolio && (
                <span>
                  <span className="text-slate-400">Acta Rechazo: </span>
                  <strong className={effectiveRejectionPrinted ? 'text-red-300' : 'text-amber-300'}>
                    {effectiveRejectionPrinted ? '✅ Impresa' : '⏳ Sin imprimir'}
                  </strong>
                </span>
              )}
              {(rampSummary.choferNombre || rampSummary.transportista || rampSummary.placaVehiculo) && (
                <span>
                  <span className="text-slate-400">Transporte: </span>
                  <strong className="text-slate-200">
                    {[rampSummary.choferNombre || rampSummary.transportista, rampSummary.placaVehiculo ? `[${rampSummary.placaVehiculo}]` : ''].filter(Boolean).join(' ')}
                  </strong>
                </span>
              )}
            </div>
          </div>
        )}

        {(stage === 'QUOTED' || stage === 'APPROVED') && (
          <div className="flex items-start gap-2 sm:col-span-2">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-400 shrink-0 mt-0.5" />
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>
                <span className="text-slate-400">Proveedor: </span>
                <strong className="text-slate-100">{vendorName || 'Por definir / Multiproveedor'}</strong>
              </span>
              {quoteNumber && (
                <span>
                  <span className="text-slate-400">Cotización: </span>
                  <strong className="font-mono text-brand-300">{quoteNumber}</strong>
                </span>
              )}
              {estimatedDeliveryDate && (
                <span>
                  <span className="text-slate-400">Entrega Est.: </span>
                  <strong className="text-sky-300">{estimatedDeliveryDate}</strong>
                </span>
              )}
              <span className="px-1.5 py-0.5 rounded bg-emerald-500/10 border border-emerald-500/30 text-[10px] text-emerald-300 font-semibold">
                🔒 Costos protegidos (RBAC)
              </span>
            </div>
          </div>
        )}
      </div>

      {/* Mensaje de Error si ocurre */}
      {errorMsg && (
        <div className="p-2.5 rounded-lg bg-red-950/50 border border-red-500/40 text-xs text-red-200 flex items-center gap-2">
          <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
          <span className="flex-1">{errorMsg}</span>
        </div>
      )}

      {/* Botones Independientes por Canal (Decisión D1) */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5 pt-0.5">
        {/* Botón 1: Telegram */}
        <button
          type="button"
          onClick={() => handleNotify('telegram')}
          disabled={sendingTelegram}
          className={`flex items-center justify-between gap-2.5 px-3.5 py-2.5 rounded-xl font-bold text-xs transition border cursor-pointer active:scale-[0.99] ${
            sentTelegram
              ? 'bg-sky-950/40 border-sky-500/50 text-sky-200 hover:bg-sky-900/50'
              : 'bg-sky-600 hover:bg-sky-500 border-sky-400/40 text-white shadow-md shadow-sky-950/50'
          } disabled:opacity-60`}
        >
          <div className="flex items-center gap-2 text-left">
            {sendingTelegram ? (
              <RefreshCw className="w-4 h-4 animate-spin text-white shrink-0" />
            ) : sentTelegram ? (
              <CheckCircle2 className="w-4 h-4 text-sky-400 shrink-0" />
            ) : (
              <Send className="w-4 h-4 text-white shrink-0" />
            )}
            <div>
              <div className="leading-tight">
                {sendingTelegram
                  ? 'Enviando a Telegram...'
                  : sentTelegram
                  ? '✅ Notificado en Telegram'
                  : '✈️ Notificar en Telegram'}
              </div>
              <div className={`text-[10px] font-normal ${sentTelegram ? 'text-sky-300/90' : 'text-sky-100/90'}`}>
                {sentTelegram || `Sanesca Producción • ${meta.telegramTopicLabel}`}
              </div>
            </div>
          </div>
          {sentTelegram && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-sky-500/20 text-sky-300 border border-sky-500/30 shrink-0">
              Reenviar
            </span>
          )}
        </button>

        {/* Botón 2: WhatsApp Grupo SANESCA ALMACEN Y DEPOSITO */}
        <button
          type="button"
          onClick={() => handleNotify('whatsapp')}
          disabled={sendingWhatsapp}
          className={`flex items-center justify-between gap-2.5 px-3.5 py-2.5 rounded-xl font-bold text-xs transition border cursor-pointer active:scale-[0.99] ${
            sentWhatsapp
              ? 'bg-emerald-950/40 border-emerald-500/50 text-emerald-200 hover:bg-emerald-900/50'
              : 'bg-emerald-600 hover:bg-emerald-500 border-emerald-400/40 text-white shadow-md shadow-emerald-950/50'
          } disabled:opacity-60`}
        >
          <div className="flex items-center gap-2 text-left">
            {sendingWhatsapp ? (
              <RefreshCw className="w-4 h-4 animate-spin text-white shrink-0" />
            ) : sentWhatsapp ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            ) : (
              <MessageCircle className="w-4 h-4 text-white shrink-0" />
            )}
            <div>
              <div className="leading-tight">
                {sendingWhatsapp
                  ? 'Enviando a WhatsApp...'
                  : sentWhatsapp
                  ? '✅ Notificado en WhatsApp'
                  : '📲 Notificar en WhatsApp'}
              </div>
              <div className={`text-[10px] font-normal ${sentWhatsapp ? 'text-emerald-300/90' : 'text-emerald-100/90'}`}>
                {sentWhatsapp || 'Grupo: SANESCA ALMACEN Y DEPOSITO'}
              </div>
            </div>
          </div>
          {sentWhatsapp && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/20 text-emerald-300 border border-emerald-500/30 shrink-0">
              Reenviar
            </span>
          )}
        </button>
      </div>
    </div>
  );
};
